import { ChangeEvent, Component } from "react"
import { Row, Col, Container, Button, Form } from "react-bootstrap"
import { BehaviorSubject, Subscription, of, concat, combineLatest } from "rxjs"
import { catchError, filter, map, switchMap } from "rxjs/operators"
import { toast } from "react-toastify"
import LocationsListGet, { LocationsListGetParams } from "../../api/Location/LocationsListGet"
import CollectionSite from "../../api/Location/LocationModel/CollectionSite"
import AddressSearch from "./AddressSearch"
import Filter from "./Filter"
import SiteCard from "./SiteCard"
import SiteServices from "./SiteServices"
import { ServiceDataModel } from "../OrderRequests/OrderModels"
import { EventType } from "../../config/Constants"
import MapContainer from "./MapContainer"
import { FaChevronLeft, FaChevronRight, FaChevronUp, FaChevronDown } from "react-icons/fa"
import { ClinicSearchOptionsGetObservable, ClinicSearchPriceForSiteSearchGetObservable  } from "../../api/Clinics/ClinicOptionsGet"
import ClinicSearchOptions from "../../api/Clinics/Models/ClinicSearchOptions"
import { ServiceCode } from "../../api/Administration/Model/Service"
import { IsServiceTypeCodeAlcohol, IsServiceTypeCodeDrug, IsServiceTypeCodePhysical } from "../../api/Administration/Model/ServiceType"
import { SSOOrderRequestData, EmptySSOOrderRequestData, EmbededTestEventWithDetail, GetEmbededTestEventWithDetail, FFSiteSearchData, GetFFTestData } from "../../api/OrderRequests/SSOOrderRequest"
import { APIError } from "../../api/ObservableFromFetch"
import ClinicSearchPrice from "../../api/Clinics/Models/ClinicSearchPrice"
import { GetSSOToken } from "../../storage/AuthToken"
import { SampleType } from "../../api/OrderRequests/OrderRequestsModel/SampleType"
import Checkbox from "../Checkbox"
import _lodash from "lodash"

export type FilterFunction = (site: CollectionSite) => boolean

interface SiteSearchProps {
  tests?: ServiceDataModel[]
  defaultAddress?: string
  isSSO?: boolean
  onSiteSelect?: (site: CollectionSite, callbackParams: {сallbackLink?: string, referenceTestID: number} ) => void
  formFoxTestAN?: number
  isFFOrder?: boolean
  forceShowFilter: boolean
  accountID?: number
}

interface Geolocation {
  available?: boolean
  enabled?: boolean
  latitude?: any
  longitude?: any
  error?: string
}

interface SiteSearchState {
  isFilterShown: boolean
  isMapShown: boolean
  siteServices: string[]
  sites: CollectionSite[]
  filteredSites: CollectionSite[]
  filter?: FilterFunction
  isLocationsSearching: boolean
  dataLoaded: boolean
  radius: number
  clinicSearchOptions?: ClinicSearchOptions
  ssoOrderRequest: SSOOrderRequestData
  searchCompleted: boolean
  tests?: EmbededTestEventWithDetail[]
  FFSiteSearchData?: FFSiteSearchData
  defaultAddress?: string
  сallbackLink?: string
  referenceTestID?: number
  priceTiers?: ClinicSearchPrice[]
  priceTiersOverride?: string[]
  customRadius: number
  geolocation?: Geolocation
}

const INITIAL_RADIUS = 15

export default class SiteSearch extends Component<SiteSearchProps, SiteSearchState> {
  state: SiteSearchState = {
    isFilterShown: true,
    isMapShown: true,
    siteServices: [],
    sites: [],
    filteredSites: [],
    radius: INITIAL_RADIUS,
    isLocationsSearching: false,
    ssoOrderRequest: EmptySSOOrderRequestData,
    searchCompleted: false,
    dataLoaded: false,
    customRadius: 0,
    geolocation: { available: !!navigator.geolocation }
  }
  locationsQuery$ = new BehaviorSubject<string | undefined>(undefined)
  radius$ = new BehaviorSubject<number>(INITIAL_RADIUS)
  locationsSubscription?: Subscription
  radiusOptionsSubscription?: Subscription
  testEventSubscription?: Subscription
  ssoOrderSubscription?: Subscription
  priceTierSubscription?: Subscription

  componentDidMount() {
    this.locationsSubscription = combineLatest([
      this.locationsQuery$.pipe(
        filter(query => query !== undefined),
        map((query) => query!.replace(/\s+/g, " ").trim()),
        filter(query => !!query)
      ),
      this.radius$
    ]).pipe(
      filter(_ => this.isSearchAllowed()),
      switchMap(([query, radius]) => concat(
        of({ isLoading: true }),
        LocationsListGet(this.buildSearchCriteria(query, radius)).pipe(
          map((data) => ({ isLoading: false, result: data })),
          catchError(() => of({ isLoading: false }))
        )))
    ).subscribe(
      (o: { isLoading: boolean, result?: CollectionSite[] }) => {
        this.setState({ isLocationsSearching: o.isLoading, searchCompleted: !o.isLoading })
        !!o.result && this.handleSearchCompleted(o.result)
      },
      (error) => {
        this.setState({ isLocationsSearching: false })
        error && error.message && toast.error(error.message)
      }
    )
    this.radiusOptionsSubscription = ClinicSearchOptionsGetObservable().subscribe(
      (response: ClinicSearchOptions) => {
        this.setState({ clinicSearchOptions: response, radius: response.SearchRadius1 || INITIAL_RADIUS })
        if (this.props.formFoxTestAN || !!this.props.isFFOrder) {
          if (this.props.isFFOrder) {
            var token = GetSSOToken()
            this.testEventSubscription = GetFFTestData(token).subscribe(
              (data: FFSiteSearchData) => this.setState({
                referenceTestID: data.ReferenceTestID,
                сallbackLink: data.CallbackLink,
                defaultAddress: data.DefaultAddress,
                radius: data.Radius,
                priceTiersOverride: data.PriceTierOverride,
                clinicSearchOptions: {SearchRadius1: data.Radius, SearchRadius2: data.Radius},
                dataLoaded: true
              }),
              error => this.handleAPIError(error)
            )
          }
          else {
            this.testEventSubscription = GetEmbededTestEventWithDetail(this.props.formFoxTestAN || 0).subscribe(
              (data: EmbededTestEventWithDetail[]) => this.setState({tests: data, dataLoaded: true}),
              error => this.handleAPIError(error)
            )
          }
        }
      },
      (error: string) => toast.error(error)
    )

    this.priceTierSubscription = ClinicSearchPriceForSiteSearchGetObservable().subscribe(
      (response: ClinicSearchPrice[]) => this.setState({ priceTiers: response }),
      (error: string) => toast.error(error)
    )

    if (this.props.formFoxTestAN) {
      this.testEventSubscription = GetEmbededTestEventWithDetail(this.props.formFoxTestAN).subscribe(
        (data: EmbededTestEventWithDetail[]) => this.setState({tests: data}),
        error => this.handleAPIError(error)
      )
    }

  }

  componentWillUnmount() {
    this.locationsSubscription && this.locationsSubscription.unsubscribe()
    this.radiusOptionsSubscription && this.radiusOptionsSubscription.unsubscribe()
    this.testEventSubscription && this.testEventSubscription.unsubscribe()
    this.ssoOrderSubscription && this.ssoOrderSubscription.unsubscribe()
    this.priceTierSubscription && this.priceTierSubscription.unsubscribe()
  }

  componentDidUpdate(prevProps: SiteSearchProps, prevState: SiteSearchState) {
    if (this.state.radius !== prevState.radius) {
      this.radius$.next(this.state.radius)
    }
  }

  handleAPIError = (error: Error | APIError) => {
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  buildSearchCriteria = (query: string, radius: number): LocationsListGetParams => {
    if (this.props.tests) {
      const { tests } = this.props

      const serviceCodes = tests.map(test => {
        if (test.selectedTestType && [(SampleType.SD, SampleType.SLD)].includes(test.selectedTestType)) {
          if (test.serviceCode === ServiceCode.Drug) {
            return ServiceCode.NonDOTOral
          } else if (test.serviceCode === ServiceCode.DrugDOT) {
            return ServiceCode.DOTOral
          }
        }
        return test.serviceCode
      })

      return {
        Address: query, //anything in the address line from UI goes in this parameter
        Radius: radius, //miles selected for search
        LabCode: tests.find(item => IsServiceTypeCodeDrug(item.serviceTypeCode))?.labAccountObj?.LabCode, //lab code from the lab account selected
        IsObserved: !!tests.find(item => IsServiceTypeCodeDrug(item.serviceTypeCode) && item.observedCollection),
        Codes: serviceCodes,
        IsSSO: !!this.props.isSSO || !!this.props.formFoxTestAN,
        ReferenceTestID: 0,
        IsFFOrder: false,
        AccountID: this.props.accountID,
        PriceTierOverride: []
      }
    }
    else if (!!this.props.isFFOrder) {
      const { referenceTestID } = this.state
      return {
        Address: query,
        Radius: radius,
        LabCode: "CRL",//other labs not supported at this time, hard coding will have to be removed if other labs added in future
        IsObserved: false,
        Codes: [],
        ReferenceTestID: referenceTestID || 0,
        IsSSO: !!this.props.isSSO,
        IsFFOrder: true,
        AccountID: this.props.accountID,
        PriceTierOverride: this.state.priceTiersOverride || [],
        Latitude: this.state.geolocation?.enabled ? this.state.geolocation?.latitude ?? "0.0" : null,
        Longitude: this.state.geolocation?.enabled ? this.state.geolocation?.longitude ?? "0.0" : null
      }
    }
    else if (this.state.tests) {
      const { tests } = this.state
      return {
        Address: query,
        Radius: radius,
        LabCode: tests.find(item => IsServiceTypeCodeDrug(item.TestTypeDescription))?.LabCode,
        IsObserved: !!tests.find(item => IsServiceTypeCodeDrug(item.TestTypeDescription) && item.Observed),
        Codes: this.embeddedTestsCodes(tests),
        ReferenceTestID: 0,
        IsSSO: !!this.props.isSSO,
        IsFFOrder: false,
        AccountID: this.props.accountID,
        PriceTierOverride: []
      }
    }
    else {
      return {
        Address: query,
        Radius: radius,
        IsObserved: false,
        LabCode: "",
        Codes: this.state.siteServices,
        IsSSO: false,
        ReferenceTestID: 0,
        IsFFOrder: false,
        AccountID: this.props.accountID,
        PriceTierOverride: []
      }
    }
  }

  embeddedTestsCodes = (tests: EmbededTestEventWithDetail[]) => {
    let codes: string[] = []

    tests.forEach((test) => {
      if (test.IsClinical) {
        codes.push(test.FFCode)
      }
      else {
        if (IsServiceTypeCodeDrug(test.TestTypeDescription)) {
          if (test.DOT) {
            if (test.SampleTypeCode === SampleType.SD) {
              codes.push(ServiceCode.DOTOral)
            } else {
              codes.push(ServiceCode.DrugDOT)
            }
          }
          else if (test.FLEX) {
            codes.push(ServiceCode.FLEX)
          }
          else if (test.SampleTypeIsPoct) {
            codes.push(ServiceCode.POCT)
          } else {
            if (test.SampleTypeCode === SampleType.SD) {
              codes.push(ServiceCode.NonDOTOral)
            } else {
              codes.push(ServiceCode.Drug)
            }
          }
        }
        if (IsServiceTypeCodeAlcohol(test.TestTypeDescription) && test.SampleTypeCode === EventType.Breath) {
          if (test.DOT) {
            codes.push(ServiceCode.AlcoholBreathDOT)
          }
          else {
            codes.push(ServiceCode.AlcoholBreath)
          }
        }
        if (IsServiceTypeCodeAlcohol(test.TestTypeDescription) && test.SampleTypeCode === EventType.Saliva) {
          if (test.DOT) {
            codes.push(ServiceCode.AlcoholSalivaDOT)
          }
          else {
            codes.push(ServiceCode.AlcoholSaliva)
          }
        }
        if (IsServiceTypeCodePhysical(test.TestTypeDescription)) {
          if (test.DOT) {
            codes.push(ServiceCode.PhysicalDOT)
          }
          else {
            codes.push(ServiceCode.Physical)
          }
        }
      }
    })

    return codes
  }

  isSearchAllowed = () => !!this.props.tests || this.state.siteServices.length > 0 || !!this.state.tests || !!this.props.isFFOrder

  handleSearchCompleted = (sites: CollectionSite[]) => {
    this.setState({
      sites: sites,
      filteredSites: this.state.filter ? sites.filter(site => this.state.filter!(site)) : sites
    })
    if (sites.length > 0) {
      toast.success("Sites found successfully.")
    } else {
      toast.error("We are currently unable to locate any locations in your area.")
    }
  }
  handleFilterChange = (filter?: FilterFunction) => this.setState({ filter: filter, filteredSites: filter ? this.state.sites.filter(site => filter(site)) : this.state.sites })
  handleRadiusChange = (radius: number) => this.setState({ radius: radius })
  applyCustomRadius = (radius: number) => this.radius$.next(radius)
  ApplyCustomRadiusDebounced = _lodash.debounce(this.applyCustomRadius, 500)
  handleCustomRadiusChange = (radius: number) => {
    this.setState({ customRadius: radius })
    this.ApplyCustomRadiusDebounced(radius)
  }

  handleSiteSelection = (site: CollectionSite) => {
    this.props.onSiteSelect && this.props.onSiteSelect(site, {сallbackLink: this.state.сallbackLink, referenceTestID: this.state.referenceTestID || 0})
  }

  handleGeoLocationChange = (e: ChangeEvent<HTMLInputElement>) => {
    const enabled = !!e.target.checked
    if (navigator.geolocation) {
      this.setState({ geolocation: { available: true, enabled: enabled } }, () => enabled && navigator.geolocation.getCurrentPosition(this.setPosition, this.setError))
    } else {
      this.setState({ geolocation: { available: false, enabled: false, error: "Geolocation is not supported by this browser." } })
    }
  }

  setPosition = (position: GeolocationPosition) => this.setState({ geolocation: {...this.state.geolocation, latitude: position.coords.latitude, longitude: position.coords.longitude } })
  setError = (error: GeolocationPositionError) => {
    var message: string
    switch (error.code) {
      case error.PERMISSION_DENIED:
        message = "This site has been blocked from accessing your location."
        break;
      case error.POSITION_UNAVAILABLE:
        message = "Location information is unavailable."
        break;
      case error.TIMEOUT:
        message = "The request to get your location timed out."
        break;
      default:
        message = "An unknown error occurred."
        break;
    }
    message && toast.error(message)
    this.setState({ geolocation: { error: message, available: false, enabled: false } })
  }

  render() {
    const isSSO = this.props.isSSO || !!this.props.formFoxTestAN || !!this.props.isFFOrder
    const showFilter = !isSSO || this.props.forceShowFilter
    return (
      <Container fluid>
        <Row className="d-none d-lg-flex">
          {showFilter && this.state.isFilterShown && <Col xs={12} sm={12} md={12} lg={3} xl={2} className="border-top-0 border-left-0 border-right-0 border-bottom border-style-dotted">
            <div className="secondary py-2 caption-5 float-left">Hierarchy</div>
            <Button variant="link" className="secondary position-absolute top-0 right-0" onClick={() => this.setState({ isFilterShown: false })}><FaChevronLeft /></Button>
          </Col>}
          <Col>
            {!this.state.isFilterShown && <Button variant="link" className="secondary float-left pl-0" onClick={() => this.setState({ isFilterShown: true })}><FaChevronRight /></Button>}
            <div className="primary pt-2 caption-5 float-left">FIND A SITE</div>
            {!this.state.isMapShown && <Button variant="link" className="secondary position-absolute top-0 right-0" onClick={() => this.setState({ isMapShown: true })}><FaChevronLeft /></Button>}
          </Col>
          {this.state.isMapShown && <Col xs={12} sm={12} md={12} lg={5} xl={5}>
            <Button variant="link" className="secondary position-absolute top-0 left-0" onClick={() => this.setState({ isMapShown: false })}><FaChevronRight /></Button>
          </Col>}
        </Row>
        <Row className="d-flex d-lg-none">
          <Col>
            <Button variant="link" className="secondary float-left pl-0" onClick={() => this.setState({ isFilterShown: !this.state.isFilterShown })}>
              {this.state.isFilterShown && <FaChevronUp />}
              {!this.state.isFilterShown && <FaChevronDown />}
            </Button>
            <div className="primary pt-2 caption-5 float-left">FIND A SITE</div>
          </Col>
        </Row>
        <Row>
          {showFilter && this.state.isFilterShown && <Col xs={12} sm={12} md={12} lg={3} xl={2}>
            <Filter
              sites={this.state.sites}
              radius={this.state.radius}
              filter={this.state.filter}
              clinicSearchOptions={this.state.clinicSearchOptions}
              priceTiers={this.state.priceTiers || []}
              onRadiusChanged={this.handleRadiusChange}
              onFilterChanged={this.handleFilterChange} />
          </Col>}
          <Col>
            {(((this.props.formFoxTestAN || !!this.props.isFFOrder) && this.state.dataLoaded) || (!this.props.formFoxTestAN && !this.props.isFFOrder)) && <AddressSearch
              onSearch={query => this.locationsQuery$?.next(query)}
              isSearching={this.state.isLocationsSearching}
              disabledSearch={!this.isSearchAllowed()}
              defaultAddress={this.props.defaultAddress || this.state.defaultAddress}
              isSSO={isSSO}
              />}
            {isSSO && <Row className="justify-content-md-center mt-1">
              <Col xs={6} className="d-flex justify-content-end">
                <Form.Label htmlFor="customSearchRadius">Radius (Miles):</Form.Label>
              </Col>
              <Col xs={2}>
                <Form.Control id="customSearchRadius"
                  type="text"
                  size="sm"
                  maxLength={2}
                  value={this.state.customRadius || ""}
                  onChange={e => this.handleCustomRadiusChange(+e.target.value)} />
              </Col>
              <Col xs={4} className="align-self-end d-flex justify-content-end">
                {/*<Checkbox id="useCurrentLocation" label="Use current location" checked={!!this.state.geolocation?.enabled} onChange={this.handleGeoLocationChange} title={this.state.geolocation?.error} />*/}
              </Col>
            </Row>}
            <SiteServices hidden={!!this.props.tests || isSSO} services={this.state.siteServices} onChange={services => this.setState({ siteServices: services })} />
            <Container fluid className="pr-4 mt-4 site-card-container">
              {this.state.filteredSites.map((site, index) => (
                <Row key={`${site.Code}_${index}`}>
                  <Col className="pr-0">
                    <SiteCard site={site} onSiteSelected={this.props.onSiteSelect ? this.handleSiteSelection : undefined} />
                  </Col>
                </Row>))}
                {isSSO && this.state.searchCompleted && !this.state.filteredSites.length &&
                  <div className="note-message font-italic px-0">If you are unable to locate a site, please call your program administrator for assistance.</div>}
            </Container>
          </Col>
          <Col xs={12} className="d-flex d-lg-none">
            <Button variant="link" className="secondary float-left pl-0" onClick={() => this.setState({ isMapShown: !this.state.isMapShown })}>
              {this.state.isMapShown && <FaChevronUp />}
              {!this.state.isMapShown && <FaChevronDown />}
            </Button>
            <div className="pt-2 caption-5 float-left">MAP</div>
          </Col>
          {this.state.isMapShown && <Col xs={12} sm={12} md={12} lg={5} xl={5}>
            <MapContainer sites={this.state.filteredSites} />
          </Col>}
        </Row>
      </Container>
    )
  }

  static defaultProps = { forceShowFilter: false }
}
